import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { ReportGroupRow } from "./report-group-row";
import { ReportVersionRow } from "./report-version-row";
import { ReportGroupData, ReportVersion } from "./types";

/** 펼쳤을 때 먼저 보여줄 이전 버전 수. 초과분은 "이전 버전 더보기"로 노출. */
const INITIAL_VISIBLE_VERSIONS = 10;

interface ReportGroupProps {
  group: ReportGroupData;
  onOpenVersion?: (version: ReportVersion) => void;
  onVersionUpdate?: (group: ReportGroupData) => void;
  /** 버전별 ⋮ 더보기 메뉴 (container가 주입) */
  renderMenu?: (version: ReportVersion) => React.ReactNode;
  /** 작성자 이름 조회 중이면 이메일 대신 스켈레톤을 보여준다. */
  isAuthorLoading?: boolean;
}

// ============================================
// ReportGroup
// 같은 보고서의 버전들을 묶어 접기/펼치기로 보여주는 그룹.
// 펼침·"더보기" 상태는 그룹 로컬 UI 상태로 관리한다.
// 기본은 펼침 — 버전 스택이 있다는 사실 자체를 먼저 보여준다.
// ============================================

export const ReportGroup = ({
  group,
  onOpenVersion,
  onVersionUpdate,
  renderMenu,
  isAuthorLoading,
}: ReportGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAllVersions, setShowAllVersions] = useState(false);

  const { latest, olderVersions } = group;
  const visibleVersions = showAllVersions
    ? olderVersions
    : olderVersions.slice(0, INITIAL_VISIBLE_VERSIONS);
  const hiddenCount = olderVersions.length - visibleVersions.length;

  return (
    // 카드 전체가 최신 버전을 여는 클릭 영역이다. 행마다 버튼을 두는 대신 카드를 덮는
    // 투명 버튼 하나(z-[1])를 깔고, 자기 동작이 있는 것들만 그 위로 올린다 — 메인 행
    // 컨트롤 z-10, 이전 버전 행 z-[2](자기 버전을 여는 오버레이를 따로 가진다),
    // "이전 버전 더보기" z-10. 버튼이 중첩되지 않으니 이벤트 전파를 막을 일이 없다.
    <div className="relative flex flex-col gap-2 rounded-xl bg-background-neutral-alternative px-4 py-3">
      <button
        type="button"
        onClick={() => onOpenVersion?.(latest)}
        aria-label={`${latest.name} 열기`}
        className="absolute inset-0 z-[1] rounded-xl"
      />

      <ReportGroupRow
        group={group}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded((prev) => !prev)}
        onVersionUpdate={() => onVersionUpdate?.(group)}
        menu={renderMenu?.(latest)}
        isAuthorLoading={isAuthorLoading}
      />

      {isExpanded && olderVersions.length > 0 && (
        <div className="flex flex-col gap-2 pl-9">
          {visibleVersions.map((version) => (
            <ReportVersionRow
              key={version.id}
              version={version}
              onOpen={() => onOpenVersion?.(version)}
              menu={renderMenu?.(version)}
              isAuthorLoading={isAuthorLoading}
            />
          ))}

          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAllVersions(true)}
              className={cn(
                "relative z-10 flex items-center justify-center gap-1 self-center py-1.5",
                "caption-1-medium text-label-alternative hover:text-label-normal",
              )}
            >
              이전 버전 더보기
              <ChevronDown className="size-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
